import { CardSkeleton } from "@/components/atoms/CardSkeleton/CardSkeleton";
import { useOrgRole } from "@/hooks/useOrgRole";
import { ItemCard } from "@/components/molecules/ItemCard";
import { conversationLinkFor } from "@/lib/activityLabels";
import { Button } from "@/components/ui/button";
import { Persona } from "@/schemas/firestore/persona.schema";
import { Asterisk, Plus } from "lucide-react";

interface PersonaSectionProps {
  personas: Persona[];
  selectedPersonas: string[];
  isLoadingPersonas: boolean;
  isGeneratingPersonas: boolean;
  onImportPersona: () => void;
  // Personas are backend-write-only (AI generated) -- no manual
  // edit/delete/toggle is available for them.
  onEditPersona?: (id: string) => void;
  onDeletePersona?: (id: string) => void;
  onTogglePersona?: (id: string) => void;
  onGeneratePersonas: () => void;
}

export function PersonaSection({
  personas,
  selectedPersonas,
  isLoadingPersonas,
  isGeneratingPersonas,
  onImportPersona,
  onEditPersona,
  onDeletePersona,
  onTogglePersona,
  onGeneratePersonas,
}: PersonaSectionProps) {
  // Viewers can read the context but not change it; the backend gates these
  // same actions on min_role=editor, so showing them would only produce 403s.
  const { canEdit } = useOrgRole();

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-0">
          <h3 className="text-lg font-medium">Personas</h3>
          <Asterisk className="h-4 w-4 text-orange-500" />
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onImportPersona}>
              <Plus className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button
              size="sm"
              className="bg-[#5A5DF6] text-white hover:bg-[#4A4DE6]"
              onClick={onGeneratePersonas}
              disabled={isGeneratingPersonas}
            >
              {isGeneratingPersonas ? "Generating..." : "Generate"}
            </Button>
          </div>
        )}
      </div>

      {isLoadingPersonas ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Loading personas...</p>
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : personas.length === 0 ? (
        <p className="text-sm text-muted-foreground">No personas created yet</p>
      ) : (
        <div className="space-y-4">
          {personas.map((persona) =>
            persona.status === "pending" ? (
              <CardSkeleton key={persona.id} />
            ) : (
              <ItemCard
                key={persona.id}
                id={persona.id}
                conversationHref={conversationLinkFor(persona)}
                title={persona.name || "Persona"}
                description={persona.tagline}
                isSelected={selectedPersonas.includes(persona.id)}
                onToggle={canEdit ? onTogglePersona : undefined}
                onEdit={canEdit ? onEditPersona : undefined}
                onDelete={canEdit ? onDeletePersona : undefined}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}
